import type { ReactElement } from "react"
import { Fragment, useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { CheckOutlined, CloseOutlined } from "@mui/icons-material"
import DeleteOutline from "@mui/icons-material/DeleteOutline"
import { CircularProgress, Grid } from "@mui/material"
import { Button, InputText, Pagination, Select, Typography } from "@synapse-analytics/synapse-ui"
import type { VisibilityState } from "@tanstack/react-table"
import { capitalize } from "lodash"
import type { MRT_ColumnDef } from "material-react-table"
import { v4 as uuidv4 } from "uuid"

import { TableJsonSwitch } from "../../../../../components/UI/TableJsonSwitch"
import { MonacoTextEditor } from "../../../../../components/data-display/MonacoTextEditor"
import { BaseTable } from "../../../../../components/tables/BaseTable"
import { useResizeObserver } from "../../../../../hooks/useResizeObserver"
import type { WorkflowInputSchema } from "../../../../../types/custom/projects"
import { WorkflowConstantFeature } from "../../../../../types/generated/api/WorkflowConstantFeature"
import { constantFeatureTypes } from "../../../../../utils/conditionHelpers"
import type { ConstantFeature, ConstantsProps } from "../../../types/interfaces"
import { convertJsonToTableData, testJson, typeCastBasedOnType } from "./helpers"

import styles from "./Constants.module.scss"

/**
 * Constants component that manages workflow constants configuration.
 * Supports both table and JSON editing modes with pagination and search functionality.
 */
export function Constants(props: Readonly<ConstantsProps>): ReactElement {
  // Prop destructuring
  const {
    formik,
    isWorkflowsLoading,
    currentMode,
    isJsonMode,
    setIsJsonMode,
    jsonText,
    setJsonText,
    rowsPerPage,
    setRowsPerPage,
  } = props

  // Custom hooks
  const [tableContainerRef, { height }] = useResizeObserver()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSchemaDialog.constants",
  })

  // State hooks
  // Pagination and search state for table view
  const [page, setPage] = useState<number>(0)
  const [search, setSearch] = useState<string | undefined>(undefined)

  // Computed values
  // Create an array that includes both the feature data and its original index
  const filteredConstantsWithIndex = formik.values.constants.reduce(
    (acc, feature, originalIndex) => {
      if (!search || feature?.name?.toLowerCase().includes(search.toLowerCase())) {
        acc.push({ ...feature, originalIndex })
      }
      return acc
    },
    [] as (ConstantFeature & { originalIndex: number })[],
  )

  const paginatedConstantsWithIndex = filteredConstantsWithIndex
    // First, paginate the features by slicing based on current page and rows per page
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const columns: MRT_ColumnDef<WorkflowInputSchema>[] = [
    {
      header: t("table.headers.constant"),
      accessorKey: "name",
      Cell: ({ row }) => {
        // const constant = formik.values.constants[row.index]

        // Use the original index from the full features array
        const constant = paginatedConstantsWithIndex[row.index]
        const originalIndex = constant?.originalIndex

        return (
          <Grid item xs={12}>
            {currentMode !== "read" && !constant?.is_used ? (
              <InputText
                fullWidth
                hideDescription
                size="small"
                placeholder={t("table.columns.constant.inputPlaceholder")}
                id={`constants[${originalIndex}].name`}
                key={constant?.id}
                value={constant?.name}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
              />
            ) : (
              <Typography variant="p">{formik.values.constants?.length > 0 ? constant?.name : ""}</Typography>
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.type"),
      accessorKey: "type",
      Cell: ({ row }) => {
        const constant = paginatedConstantsWithIndex[row.index]
        const originalIndex = constant?.originalIndex

        return (
          <Grid item container xs={12} flexWrap="nowrap">
            {currentMode === "read" || constant?.is_used ? (
              <Typography style={{ textTransform: "capitalize" }} variant="p">
                {constant?.type?.toLowerCase()}
              </Typography>
            ) : (
              <Select
                fullWidth
                hideDescription
                type="text"
                size="small"
                placeholder={t("table.columns.type.selectPlaceholder")}
                optionsWithValues={constantFeatureTypes.map((constant) => ({
                  label: capitalize(constant),
                  value: constant,
                }))}
                disabled={constant?.is_used === true}
                id={`constants[${row?.index}].type`}
                value={constant?.type}
                handleChange={(e) => {
                  formik.setFieldValue(`constants[${originalIndex}].type`, e.target.value)

                  formik.setFieldValue(
                    `constants[${originalIndex}].value`,
                    typeCastBasedOnType(
                      constant?.value,
                      (e.target.value as string).toUpperCase() as WorkflowConstantFeature.type,
                    ),
                  )
                }}
              />
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.value"),
      accessorKey: "value",
      Cell: ({ row }) => {
        const constant = paginatedConstantsWithIndex[row.index]
        const originalIndex = constant?.originalIndex

        if (constant?.type === WorkflowConstantFeature.type.LIST) {
          return <Typography variant="a">{t("table.columns.value.cannotViewList")}</Typography>
        }

        return (
          <Grid item xs={12}>
            {currentMode !== "read" ? (
              constant?.type === WorkflowConstantFeature.type.BOOLEAN ? (
                <Select
                  fullWidth
                  hideDescription
                  type="text"
                  size="small"
                  options={["True", "False"]}
                  placeholder={t("table.columns.value.selectPlaceholder")}
                  id={`constants[${row?.index}].value`}
                  value={constant?.value ? "True" : "False"}
                  handleChange={(e) => {
                    formik.setFieldValue(`constants[${originalIndex}].value`, e.target.value === "True" ? true : false)
                  }}
                />
              ) : (
                <InputText
                  fullWidth
                  hideDescription
                  size="small"
                  placeholder={t("table.columns.value.inputPlaceholder")}
                  type={constant?.type === WorkflowConstantFeature.type.NUMBER ? "number" : "text"}
                  id={`constants[${originalIndex}].value`}
                  key={constant?.id}
                  value={String(constant?.value)}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                />
              )
            ) : (
              <Typography variant="p">{`${constant?.value ?? ""} `}</Typography>
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.usageStatus"),
      accessorKey: "is_used",
      Cell: ({ row }) => {
        const constant = paginatedConstantsWithIndex[row.index]

        return (
          <Grid item display="flex" gap={0.75} alignItems="center">
            {constant?.is_used ? (
              <Fragment>
                <CheckOutlined className={styles.green} fontSize="small" />
                <Typography variant="p">{t("table.columns.usageStatus.inUse")}</Typography>
              </Fragment>
            ) : (
              <Fragment>
                <CloseOutlined className={styles.red} fontSize="small" />
                <Typography variant="p">{t("table.columns.usageStatus.unused")}</Typography>
              </Fragment>
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.action"),
      accessorKey: "action",
      Cell: ({ row }) => {
        const constant = paginatedConstantsWithIndex[row.index]
        const isButtonDisabled = currentMode === "read" || constant?.is_used === true

        return (
          <Grid item xs={12} container flexWrap={"nowrap"}>
            <Button
              tooltip={isButtonDisabled ? t("table.columns.actions.tooltips.cannotDeleteUsedConstant") : ""}
              tooltipPlacement="top"
              variant={"ghost"}
              size="small"
              onClick={() => {
                formik.setFieldValue(
                  `constants`,
                  formik.values.constants.filter((constantFeature) => constantFeature?.id !== constant?.id),
                )
              }}
              className={styles.iconButton}
              disabled={isButtonDisabled}
            >
              <DeleteOutline
                style={{
                  fontSize: "16px",
                  fill: !isButtonDisabled
                    ? "var(--negative-icon-default) !important"
                    : "var(--disabled-icon-default) !important",
                }}
              />
            </Button>
          </Grid>
        )
      },
    },
  ]

  const columnVisibility: VisibilityState | undefined = useMemo(() => {
    switch (currentMode) {
      case "read":
        return {
          name: true,
          type: true,
          value: true,
          is_used: true,
          action: false,
        }
      default:
        return {
          name: true,
          type: true,
          value: true,
          is_used: true,
          action: true,
        }
    }
  }, [currentMode])

  const convertTableDataToJson = useCallback((): void => {
    // Only include constants that are not in use
    const constants = formik.values.constants.reduce(
      (acc, constant) => ({ ...acc, [constant.name]: constant.value }),
      {},
    )

    setJsonText(JSON.stringify(constants, null, 2))
  }, [formik.values.constants, setJsonText])

  const handleSwitchMode = (): void => {
    // now we're in json mode and we want to switch to test features
    if (isJsonMode) {
      const hasPassed = testJson(jsonText, t("notifications.switchModeSchemaError"))

      if (!hasPassed) {
        return // Exit if JSON validation fails
      }

      formik.setFieldValue("constants", convertJsonToTableData(jsonText, formik.values.constants)) // convert JSON to table data
      setSearch(undefined) // Clear search when switching back to table mode
      setPage(0)
    } else {
      convertTableDataToJson()
    }

    setIsJsonMode(!isJsonMode)
  }

  useEffect(() => {
    if (height && tableContainerRef.current) {
      const headerEl = tableContainerRef.current.querySelector<HTMLElement>(".MuiTableHead-root")
      const rowEl = tableContainerRef.current.querySelector<HTMLElement>("tbody .MuiTableRow-root")

      const headerHeight = headerEl?.offsetHeight ?? 33
      const rowHeight = rowEl?.offsetHeight ?? 36

      if (rowHeight > 0) {
        const tableBodyHeight = height - headerHeight
        const newRowsPerPage = Math.floor(tableBodyHeight / rowHeight)

        if (newRowsPerPage > 0) {
          setRowsPerPage(newRowsPerPage)
        } else {
          setRowsPerPage(10) // Ensure at least one row is shown
        }
      }
    }
    // Rerun when data changes to get row height when data appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, tableContainerRef])

  return (
    <Grid item container gap={2} xs direction={"column"} flexWrap={"nowrap"}>
      <Grid container item justifyContent={"space-between"} alignItems="center" flexWrap={"nowrap"}>
        {!isJsonMode && (
          <InputText
            variant="filled"
            placeholder={t("searchPlaceholder")}
            hideDescription
            value={search}
            type="text"
            handleChange={(e) => {
              setSearch(e.target.value as string)
              setPage(0) // Reset to first page when search changes
            }}
          />
        )}

        <Grid item container gap={2} alignItems="center" justifyContent={"flex-end"}>
          {!isJsonMode && currentMode !== "read" && (
            <Button
              size="small"
              variant="secondary"
              onClick={() => {
                formik.setFieldValue("constants", [
                  { name: "", type: "TEXT", value: "", is_used: false, id: uuidv4(), new: true },
                  ...formik.values.constants,
                ])
                setPage(0)
              }}
              disabled={!!search}
              tooltip={search ? t("addConstantButton.tooltip") : ""} // Disable button if search is active
            >
              {t("addConstantButton.text")}
            </Button>
          )}

          <TableJsonSwitch isJsonMode={isJsonMode} handleSwitchMode={handleSwitchMode} />
        </Grid>
      </Grid>

      {!isJsonMode ? (
        <Fragment>
          <Grid item xs={12}>
            <BaseTable
              newStyling
              disableToolbar
              disablePagination
              enableOrdering={false}
              title={t("table.title")}
              pageSize={rowsPerPage}
              columns={(columns as MRT_ColumnDef[]) ?? []}
              data={paginatedConstantsWithIndex ?? []}
              isLoading={currentMode === "read" && isWorkflowsLoading}
              columnsVisibility={columnVisibility}
            />
          </Grid>

          {filteredConstantsWithIndex.length > 0 && (
            <Pagination
              page={page + 1}
              onPageChange={(page: number) => setPage(page - 1)}
              pageSize={rowsPerPage}
              totalCount={filteredConstantsWithIndex.length} // Use filtered count, not paginated
              variant={"simple"}
              size="small"
              hidePageSize
              maxVisiblePages={5}
            />
          )}
        </Fragment>
      ) : (
        <Grid container item className={styles.jsonEditorContainer} flexDirection={"column"}>
          <Grid item p={2} bgcolor={"var(--base-background-1)"}>
            <Typography variant={"a"}>{t("jsonEditorTitle")}</Typography>
          </Grid>
          <Grid container item xs>
            <MonacoTextEditor
              key={`${formik.values.workflowName}-constants`}
              value={jsonText}
              language="json"
              onChange={(text) => setJsonText(text ?? "")}
              enableCopy
              renderLineHighLight="none"
              wordWrap="on"
              width="100%"
              loading={<CircularProgress style={{ color: "rgb(25 118 210)" }} size={36} />}
              readOnly={currentMode === "read"}
              borderRadius="0px 0px 8px 8px"
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}
